import { Database, FileText, Lightbulb, MessageSquare, Notebook } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import ProjectsPromptDialog from './ProjectsPromptDialog';
import { filesystemService } from '../../utils/filesystem';
import { rootStore } from '../../stores/RootStore';
import { toast } from 'sonner';

interface NotebookOutput {
    output_type: string;
    data?: Record<string, unknown>;
    text?: string | string[];
    execution_count?: number;
}

interface NotebookCell {
    cell_type: 'code' | 'markdown' | 'raw';
    source: string | string[];
    outputs?: NotebookOutput[];
    metadata?: Record<string, unknown>;
}

interface ProjectsMenuProps {
    filter: 'all' | 'published' | 'drafts';
    onFilterChange: (filter: 'all' | 'published' | 'drafts') => void;
    onCreateNewProject: (type: string, prompt?: string) => void;
    onFilesUpdated?: () => void;
    initialPrompt?: string;
}

const ProjectsMenu: React.FC<ProjectsMenuProps> = ({
    filter,
    onFilterChange,
    onCreateNewProject,
    onFilesUpdated,
    initialPrompt
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isPromptDialogOpen, setIsPromptDialogOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const notebookInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handlePromptSubmit = useCallback(async (prompt: string) => {
        try {
            await onCreateNewProject('prompt', prompt);
            rootStore.uiStore.initialAgentPrompt = prompt;
            rootStore.uiStore.setAgentVisible(true);

            setIsPromptDialogOpen(false);
            setIsDropdownOpen(false);
        } catch (error) {
            console.error('Error creating project:', error);
            toast.error('Failed to create project');
        }
    }, [onCreateNewProject]);

    useEffect(() => {
        if (initialPrompt) {
            // For initial prompts from URL (like from landing page flow),
            // just call the create project function directly without duplicating the prompt setting
            // The AgentPanel will handle the prompt automatically via uiStore.initialAgentPrompt
            onCreateNewProject('prompt', initialPrompt);
            setIsDropdownOpen(false);
        }
    }, [initialPrompt, onCreateNewProject]);

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'data' | 'notebook') => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];
        const isNotebook = type === 'notebook';

        if (isNotebook && !file.name.endsWith('.ipynb')) {
            toast.error('Please upload a Jupyter notebook (.ipynb) file');
            return;
        } else if (!isNotebook && !file.name.endsWith('.csv')) {
            toast.error('Please upload a CSV file');
            return;
        }

        setIsUploading(true);
        try {
            // Create project first
            await onCreateNewProject(type);

            // For notebooks, place in root directory
            const filePath = isNotebook ? `/project/${file.name}` : `/project/data/${file.name}`;

            // For data files, ensure data directory exists
            if (!isNotebook) {
                await filesystemService.createDirectory('/project/data');
            }

            const content = await file.text();

            // Write the file to the filesystem
            await filesystemService.writeFileToPyodide(filePath, content);

            // For notebooks, set initial prompt to convert to preswald app
            if (isNotebook) {
                const displayContext = `
Here's how to display content in a Preswald app:

import preswald

preswald.text("# Title")
preswald.text("## Subtitle")
preswald.text("Description")

preswald.plotly(fig)
`;

                // Parse and truncate notebook content
                try {
                    const notebookData = JSON.parse(content);
                    const cells = notebookData.cells || [];
                    
                    // Take first 5 cells and only include essential information
                    const truncatedCells = cells.slice(0, 5).map((cell: NotebookCell) => ({
                        cell_type: cell.cell_type,
                        source: cell.source,
                        outputs: cell.outputs ? cell.outputs.slice(0, 1) : undefined // Only take first output
                    }));

                    const defaultPrompt = `Convert this notebook into a Preswald app. Use the preswald display functions to show the outputs.

Notebook preview (first 5 cells):
${JSON.stringify(truncatedCells, null, 2)}

${displayContext}`;
                    rootStore.uiStore.initialAgentPrompt = defaultPrompt;
                    rootStore.uiStore.setAgentVisible(true);
                } catch (error) {
                    console.error('Error parsing notebook:', error);
                    // Fallback to a simpler prompt if notebook parsing fails
                    const defaultPrompt = `Convert this notebook into a Preswald app. Use the preswald display functions to show the outputs.

${displayContext}`;
                    rootStore.uiStore.initialAgentPrompt = defaultPrompt;
                    rootStore.uiStore.setAgentVisible(true);
                }
            } else {
                // For data files, read first few rows and create a prompt
                const lines = content.split('\n').slice(0, 6); // Header + 5 rows
                const dataPreview = lines.join('\n');
                const defaultPrompt = `Generate a report with 3 plots using plotly with the following data:\n\n${dataPreview}`;
                rootStore.uiStore.initialAgentPrompt = defaultPrompt;
                rootStore.uiStore.setAgentVisible(true);
            }

            // Save to IndexedDB to persist the changes
            await filesystemService.saveToIndexedDB();

            // Trigger file explorer refresh
            if (onFilesUpdated) {
                onFilesUpdated();
            }

            setIsDropdownOpen(false);
            toast.success('File uploaded successfully');
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error('Failed to upload file');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            if (notebookInputRef.current) {
                notebookInputRef.current.value = '';
            }
        }
    };

    const menuItems = [
        { id: 'all' as const, label: 'All' },
        { id: 'published' as const, label: 'Published' },
        { id: 'drafts' as const, label: 'Drafts' }
    ];

    const projectOptions = [
        { label: 'Blank project', action: onCreateNewProject, type: 'blank', icon: FileText },
        { label: 'Example project', action: onCreateNewProject, type: 'example', icon: Lightbulb },
        { label: 'Import from Notebook', action: () => notebookInputRef.current?.click(), type: 'notebook', icon: Notebook },
        { label: 'Generate from Prompt', action: () => setIsPromptDialogOpen(true), type: 'prompt', icon: MessageSquare },
        { label: 'Generate from Data', action: () => fileInputRef.current?.click(), type: 'data', icon: Database }
    ];

    return (
        <div className="flex justify-between items-center mb-6">
            <div>
            </div>
            <div className="flex gap-2">
                {menuItems.map((item) => (
                    <button
                        key={item.id}
                        className={`px-2 py-1 text-sm rounded-md border transition-all ${filter === item.id
                            ? 'bg-gray-100 border-gray-300 font-medium'
                            : 'bg-transparent border-gray-200 hover:bg-gray-50'
                            }`}
                        onClick={() => onFilterChange(item.id)}
                    >
                        {item.label}
                    </button>
                ))}
                <div className="relative" ref={dropdownRef}>
                    <button
                        className="px-2 py-1 text-sm bg-gray-800 text-white rounded-md font-medium hover:bg-gray-700 transition-colors"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                        New Project
                    </button>
                    {isDropdownOpen && (
                        <div className="absolute right-0 mt-1 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                            {projectOptions.map((option, index) => {
                                const Icon = option.icon;
                                return (
                                    <button
                                        key={index}
                                        className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                                        onClick={() => {
                                            if (option.type === 'data') {
                                                fileInputRef.current?.click();
                                            } else if (option.type === 'notebook') {
                                                notebookInputRef.current?.click();
                                            } else if (option.type === 'prompt') {
                                                setIsPromptDialogOpen(true);
                                            } else {
                                                option.action(option.type);
                                                setIsDropdownOpen(false);
                                            }
                                        }}
                                        disabled={(option.type === 'data' || option.type === 'notebook') && isUploading}
                                    >
                                        <Icon className={`w-4 h-4 ${(option.type === 'data' || option.type === 'notebook') && isUploading ? 'animate-spin' : ''}`} />
                                        {option.label}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => handleFileUpload(e, 'data')}
                    accept=".csv"
                    className="hidden"
                />
                <input
                    type="file"
                    ref={notebookInputRef}
                    onChange={(e) => handleFileUpload(e, 'notebook')}
                    accept=".ipynb"
                    className="hidden"
                />
            </div>

            <ProjectsPromptDialog
                isOpen={isPromptDialogOpen}
                onClose={() => setIsPromptDialogOpen(false)}
                onSubmit={handlePromptSubmit}
            />
        </div>
    );
};

export default ProjectsMenu; 